import { Select, SelectProps, SkeletonBox } from '@billy/design-system'
import { forwardRef, Ref, useCallback, useMemo } from 'react'

import { EInvoiceAddressSchemeType } from '../../../../enums/eInvoiceAddressSchemeType'
import { useFetchEInvoiceAddressSchemeTypeList } from './hooks/useFetchEInvoiceAddressSchemeTypeList'
import { getEInvoiceAddressSchemeType } from './utils/getEInvoiceAddressSchemeType'

interface EInvoiceAddressSchemeTypeSelectProps extends Omit<SelectProps<any>, 'items' | 'onSelect'> {
  countryId?: string
  onSelect?: (id?: string, value?: EInvoiceAddressSchemeType) => void
}

export const EInvoiceAddressSchemeTypeSelect = forwardRef(
  (
    { countryId, defaultSelectedId, onSelect, selectedId, ...selectProps }: EInvoiceAddressSchemeTypeSelectProps,
    ref: Ref<HTMLDivElement | null>,
  ) => {
    const { eInvoiceAddressSchemeTypeList, isLoading: isEInvoiceAddressLoading } =
      useFetchEInvoiceAddressSchemeTypeList()

    const eInvoiceAddressSchemeTypes = useMemo(() => {
      return getEInvoiceAddressSchemeType({ countryId, eInvoiceAddressSchemeTypeList })
    }, [countryId, eInvoiceAddressSchemeTypeList])

    const selectedItem = useMemo(
      () => eInvoiceAddressSchemeTypes.find(({ value }) => selectedId === value),
      [selectedId, eInvoiceAddressSchemeTypes],
    )

    const handleInvoiceAddressSelect = useCallback(
      (id?: string, value?: EInvoiceAddressSchemeType) => {
        onSelect?.(id, value)
      },
      [onSelect],
    )

    if (isEInvoiceAddressLoading) {
      return <SkeletonBox fullWidth height={40} />
    }

    return (
      <Select
        {...selectProps}
        defaultSelectedId={defaultSelectedId}
        items={eInvoiceAddressSchemeTypes}
        onSelect={handleInvoiceAddressSelect}
        ref={ref}
        selectedId={selectedItem?.id}
      />
    )
  },
)
